import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface LegendEntry {
  id: string;
  name: string;
  color: string | null | undefined;
}

export interface LegendOrderData {
  entries: LegendEntry[];
  selectedEntryIds: Set<string>;
}

interface LegendEntriesContextType {
  /**
   * Get legend entries for a specific order.
   * Returns undefined if entries are not yet available.
   */
  getEntriesForOrder: (orderId: string) => LegendEntry[] | undefined;

  /**
   * Get selected entry IDs for a specific order.
   * Returns an empty set if no selection.
   */
  getSelectedEntryIds: (orderId: string) => Set<string>;

  /**
   * Set legend entries for an order.
   * Called by order panels after data is resolved.
   */
  setEntriesForOrder: (orderId: string, entries: LegendEntry[]) => void;

  /**
   * Set selected entry IDs for an order.
   * Called by order panels when row selection changes.
   */
  setSelectedEntryIds: (orderId: string, selectedIds: Set<string>) => void;

  /**
   * Clear legend entries for an order.
   * Called when leaving an order.
   */
  clearEntriesForOrder: (orderId: string) => void;
}

const LegendEntriesContext = createContext<LegendEntriesContextType | null>(
  null
);

export function LegendEntriesProvider({ children }: { children: ReactNode }) {
  const [entriesMap, setEntriesMap] = useState<Map<string, LegendEntry[]>>(
    new Map()
  );
  const [selectedIdsMap, setSelectedIdsMap] = useState<
    Map<string, Set<string>>
  >(new Map());

  const getEntriesForOrder = useCallback(
    (orderId: string) => entriesMap.get(orderId),
    [entriesMap]
  );

  const getSelectedEntryIds = useCallback(
    (orderId: string) => selectedIdsMap.get(orderId) ?? new Set<string>(),
    [selectedIdsMap]
  );

  const setEntriesForOrder = useCallback(
    (orderId: string, entries: LegendEntry[]) => {
      setEntriesMap((prev) => {
        const next = new Map(prev);
        next.set(orderId, entries);
        return next;
      });
    },
    []
  );

  const setSelectedEntryIds = useCallback(
    (orderId: string, selectedIds: Set<string>) => {
      setSelectedIdsMap((prev) => {
        const next = new Map(prev);
        next.set(orderId, selectedIds);
        return next;
      });
    },
    []
  );

  const clearEntriesForOrder = useCallback((orderId: string) => {
    setEntriesMap((prev) => {
      const next = new Map(prev);
      next.delete(orderId);
      return next;
    });
    setSelectedIdsMap((prev) => {
      const next = new Map(prev);
      next.delete(orderId);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      getEntriesForOrder,
      getSelectedEntryIds,
      setEntriesForOrder,
      setSelectedEntryIds,
      clearEntriesForOrder,
    }),
    [
      getEntriesForOrder,
      getSelectedEntryIds,
      setEntriesForOrder,
      setSelectedEntryIds,
      clearEntriesForOrder,
    ]
  );

  return (
    <LegendEntriesContext.Provider value={value}>
      {children}
    </LegendEntriesContext.Provider>
  );
}

export function useLegendEntriesContext() {
  const context = useContext(LegendEntriesContext);
  if (!context) {
    throw new Error(
      'useLegendEntriesContext must be used within a LegendEntriesProvider'
    );
  }
  return context;
}

/**
 * Hook for order panels to provide legend entries.
 * Returns a setter function that should be called when data is resolved.
 */
export function useProvideLegendEntries(orderId: string) {
  const { setEntriesForOrder, clearEntriesForOrder } =
    useLegendEntriesContext();

  const setEntries = useCallback(
    (entries: LegendEntry[]) => {
      setEntriesForOrder(orderId, entries);
    },
    [orderId, setEntriesForOrder]
  );

  const clearEntries = useCallback(() => {
    clearEntriesForOrder(orderId);
  }, [orderId, clearEntriesForOrder]);

  return { setEntries, clearEntries };
}

/**
 * Hook for legends to get entries for their order.
 */
export function useLegendEntries(orderId: string | undefined) {
  const { getEntriesForOrder } = useLegendEntriesContext();
  return orderId ? getEntriesForOrder(orderId) : undefined;
}
